import { useState } from 'react';
import { FiSmile } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import { useMemberAuth } from '../../contexts/MemberAuthContext';
import { cn } from '../../utils/helpers';

// A short fixed set rather than a full picker: it covers the overwhelming
// majority of real use and avoids pulling in an emoji-data dependency.
const QUICK_REACTIONS = ['👍', '❤️', '🙏', '🎉', '😂', '👀'];

/**
 * Existing reactions on a message, and optionally a way to add one.
 *
 * `showAdd` is false wherever a hover/long-press action bar already offers the
 * emoji picker — two pickers on the same message is just clutter. The thread
 * view has no action bar, so it keeps its own.
 */
export default function ReactionBar({ messageId, compact = false, showAdd = true }) {
  const { reactions, toggleReaction } = useChat();
  const { member } = useMemberAuth();
  const [pickerOpen, setPickerOpen] = useState(false);

  const rows = reactions[messageId] || [];

  // Group by emoji, tracking whether the current member is among the reactors —
  // that drives both the highlight and the toggle direction.
  const grouped = rows.reduce((acc, row) => {
    if (!acc[row.emoji]) acc[row.emoji] = { emoji: row.emoji, count: 0, mine: false, names: [] };
    acc[row.emoji].count += 1;
    acc[row.emoji].names.push(row.displayName || 'Someone');
    if (row.memberId === member?.id) acc[row.emoji].mine = true;
    return acc;
  }, {});

  const summary = Object.values(grouped);
  if (summary.length === 0 && !showAdd) return null;

  const react = (emoji) => {
    setPickerOpen(false);
    toggleReaction(messageId, emoji);
  };

  return (
    <div className={cn('flex items-center gap-1 flex-wrap', compact ? 'mt-0.5' : 'mt-1')}>
      {summary.map((entry) => (
        <button
          key={entry.emoji}
          onClick={() => react(entry.emoji)}
          title={entry.names.join(', ')}
          className={cn(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs transition-colors',
            entry.mine
              ? 'border-accent bg-accent/25 text-primary font-medium'
              : 'border-neutral-300 bg-white hover:bg-highlight/60 text-primary/70'
          )}
        >
          <span>{entry.emoji}</span>
          <span className="tabular-nums">{entry.count}</span>
        </button>
      ))}

      {showAdd && (
        <div className="relative">
          <button
            onClick={() => setPickerOpen((open) => !open)}
            className="p-1 rounded-full text-primary/35 hover:text-primary hover:bg-highlight transition-colors"
            aria-label="Add reaction"
            aria-expanded={pickerOpen}
          >
            <FiSmile className="w-3.5 h-3.5" />
          </button>

          {pickerOpen && (
            <>
              {/* Click-away layer, so the picker closes without a document listener. */}
              <div
                className="fixed inset-0 z-[56]"
                onClick={() => setPickerOpen(false)}
                aria-hidden="true"
              />
              <div className="absolute bottom-full left-0 mb-1 z-[57] flex gap-0.5 p-1 bg-white rounded-[10px] shadow-lg shadow-primary/10 border border-neutral-300">
                {QUICK_REACTIONS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => react(emoji)}
                    className="p-1 rounded-lg hover:bg-highlight text-base leading-none transition-colors"
                    aria-label={`React with ${emoji}`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
